import { zodResolver } from '@hookform/resolvers/zod';
import type { ComponentProps } from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { z } from 'zod';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { resetPassword } from '@/services/authService';
import { getLandingPrivacyUrl } from '@/utils/landingUrl';

export function ResetPasswordForm({
  className,
  ...props
}: ComponentProps<'div'>): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const privacyUrl = getLandingPrivacyUrl(i18n.language ?? i18n.resolvedLanguage ?? 'en');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const resetPasswordSchema = useMemo(
    () =>
      z
        .object({
          password: z.string().min(6, t('validation.passwordMin')),
          confirmPassword: z.string().min(6, t('validation.passwordMin')),
        })
        .refine((data) => data.password === data.confirmPassword, {
          message: t('auth.resetPassword.passwordMismatch'),
          path: ['confirmPassword'],
        }),
    [t],
  );
  type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (values: ResetPasswordFormValues): Promise<void> => {
    if (!token) {
      toast.error(t('auth.resetPassword.tokenMissing'));
      return;
    }

    try {
      await resetPassword(token, values.password);
      toast.success(t('auth.resetPassword.success'));
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error(t('auth.resetPassword.error'));
    }
  };

  if (!token) {
    return (
      <div className={cn('flex flex-col gap-6', className)} {...props}>
        <Card className="overflow-hidden">
          <CardContent className="grid p-0 md:grid-cols-2">
            <div className="p-6 md:p-8">
              <div className="flex flex-col gap-6">
                <div className="flex items-center justify-between gap-2 flex-col">
                  <h1 className="text-2xl font-bold">
                    {t('auth.resetPassword.title')}
                  </h1>
                  <p className="text-sm text-destructive">
                    {t('auth.resetPassword.tokenMissing')}
                  </p>
                </div>
                <div className="text-center text-sm">
                  <Link
                    to="/login"
                    className="underline underline-offset-4 text-primary"
                  >
                    {t('auth.forgotPassword.backToLogin')}
                  </Link>
                </div>
              </div>
            </div>
            <div className="hidden items-center justify-center bg-muted p-8 md:flex">
              <img
                src="/brand/trushelf-horizontal.svg"
                alt="TruShelf"
                className="w-2/3 max-w-xs"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className="overflow-hidden">
        <CardContent className="grid p-0 md:grid-cols-2">
          <form className="p-6 md:p-8" onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-6">
              <div className="flex items-center justify-between gap-2 flex-col">
                <h1 className="text-2xl font-bold">
                  {t('auth.resetPassword.title')}
                </h1>
                <p className="text-balance text-muted-foreground">
                  {t('auth.resetPassword.description')}
                </p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password">
                  {t('auth.resetPassword.password')}
                </Label>
                <Input
                  id="password"
                  type="password"
                  {...register('password')}
                />
                {errors.password ? (
                  <p className="text-sm text-destructive">
                    {errors.password.message}
                  </p>
                ) : null}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="confirmPassword">
                  {t('auth.resetPassword.confirmPassword')}
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  {...register('confirmPassword')}
                />
                {errors.confirmPassword ? (
                  <p className="text-sm text-destructive">
                    {errors.confirmPassword.message}
                  </p>
                ) : null}
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting
                  ? t('auth.resetPassword.submitting')
                  : t('auth.resetPassword.submit')}
              </Button>
              <div className="text-center text-sm">
                <Link
                  to="/login"
                  className="underline underline-offset-4 text-primary"
                >
                  {t('auth.forgotPassword.backToLogin')}
                </Link>
              </div>
            </div>
          </form>
          <div className="hidden items-center justify-center bg-muted p-8 md:flex">
            <img
              src="/brand/trushelf-horizontal.svg"
              alt="TruShelf"
              className="w-2/3 max-w-xs"
            />
          </div>
        </CardContent>
      </Card>
      <div className="text-balance text-center text-xs text-white/90 [&_a]:underline [&_a]:underline-offset-4 [&_a]:text-white [&_a]:hover:text-secondary">
        {t('auth.login.termsIntro')}{' '}
        <a href={privacyUrl} target="_blank" rel="noopener noreferrer">
          {t('auth.login.privacy')}
        </a>
        .
      </div>
    </div>
  );
}

export function ResetPasswordPage(): JSX.Element {
  return (
    <div className="bg-primary flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm md:max-w-4xl">
        <ResetPasswordForm />
      </div>
    </div>
  );
}
